import { ComponentProps, SyntheticEvent, useEffect, useState } from "react";
import {
  DragDropContext,
  Draggable,
  Droppable,
  DroppableProps,
  DropResult,
} from "react-beautiful-dnd";
import { CloseButton, Dropdown } from "react-bootstrap";

import {
  ApiCategory,
  ApiCompetitionEntryInline,
  ApiRoute,
  ApiRouteAttempt,
} from "../../../types";
import getCompetitionRouteDisplay from "../../../utils/getCompetitionRouteDisplay";
import CategorySelector from "../../CategorySelector";
import { QueueSvg } from "../../icons";
import RouteEntryItem from "../../RouteEntryItem/RouteEntryItem";
import ScreenHeader from "../../ScreenHeader/ScreenHeader";
import TextInput from "../../TextInput";
import * as Style from "./RouteEveryoneListScreen.style";
import useRouteEveryoneListScreen, {
  SortOption,
} from "./useRouteEveryoneListScreen";

type QueueItemProps = {
  route?: ApiRoute;
  entry: Omit<ApiCompetitionEntryInline, "category"> & {
    category?: ApiCategory;
    attempts?: ApiRouteAttempt[];
    matchesFilter: boolean;
  };
  climbType: "BOULDER" | "SPORT";
  index: number;
  onRemove: (uuid: string) => void;
};

const DraggableQueueItem = ({
  route,
  entry,
  climbType,
  index,
  onRemove,
}: QueueItemProps) => (
  <Draggable key={entry.user.uuid} draggableId={entry.user.uuid} index={index}>
    {({ innerRef, draggableProps, dragHandleProps }) => (
      <Style.CompetitorsQueueListItem
        key={entry.user.uuid}
        ref={innerRef}
        {...draggableProps}
        style={{
          opacity: entry.matchesFilter ? 1 : 0.2,
          ...draggableProps.style,
        }}
        {...dragHandleProps}
      >
        <RouteEntryItem
          entry={entry}
          route={route}
          climbType={climbType || "BOULDER"}
          attempts={entry.attempts}
          showHandle
        >
          <CloseButton
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onRemove(entry.user.uuid);
            }}
            className="ms-2"
          />
        </RouteEntryItem>
      </Style.CompetitorsQueueListItem>
    )}
  </Draggable>
);

const QueueStatusButton = ({
  inQueue,
  ...props
}: ComponentProps<typeof Style.QueueStatusButton>) => (
  <Style.QueueStatusButton {...props} inQueue={inQueue}>
    {inQueue ? (
      <svg
        width="14"
        height="10"
        viewBox="0 0 14 10"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M5.3833 9.66639L0.883301 5.16639L2.5665 3.48319L5.4169 6.35039L11.4665 0.299988L13.1169 1.93359L5.3833 9.66639Z"
          fill="currentColor"
        />
      </svg>
    ) : (
      <QueueSvg />
    )}
  </Style.QueueStatusButton>
);

// This was required to make react-beautiful-dnd work with react 18 strict mode.
// (taken from https://github.com/atlassian/react-beautiful-dnd/issues/2399)

const StrictModeDroppable = ({ children, ...props }: DroppableProps) => {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const animation = requestAnimationFrame(() => setEnabled(true));

    return () => {
      cancelAnimationFrame(animation);
      setEnabled(false);
    };
  }, []);

  if (!enabled) {
    return null;
  }

  return <Droppable {...props}>{children}</Droppable>;
};

const reorder = <T,>(arr: T[], startIndex: number, endIndex: number) => {
  const result = [...arr];
  const [removed] = result.splice(startIndex, 1);
  if (!removed) {
    throw new Error("This should never happen");
  }
  result.splice(endIndex, 0, removed);
  return result;
};

export default function RouteEveryoneListScreen() {
  const {
    route,
    searchText,
    setSearchText,
    selectedCategories,
    selectedCategoryUUIDs,
    availableCategories,
    setSelectedCategoryUUIDs,
    queuedEntries,
    unqueuedEntries,
    queue,
    addToQueue,
    updateQueue,
    judgePermissions,
    // isDragging,
    setIsDragging,
    sortOption,
    setSortOption,
  } = useRouteEveryoneListScreen();

  const sortOptions: { value: SortOption; label: string }[] = [
    { value: "alpha-asc", label: "Alphabetical (A-Z)" },
    { value: "alpha-desc", label: "Alphabetical (Z-A)" },
    { value: "entry-asc", label: "Entry time (oldest first)" },
    { value: "entry-desc", label: "Entry time (newest first)" },
  ];

  const onDragEnd = (result: DropResult) => {
    if (!result.destination || !queue) {
      return;
    }
    const newQueue = reorder(
      queue,
      result.source.index,
      result.destination.index,
    );
    updateQueue(newQueue);
  };

  const onRemove = (uuid: string) => {
    if (!queue) {
      return;
    }
    updateQueue(queue.filter((x) => x !== uuid));
  };

  if (judgePermissions?.canView === false) {
    return (
      <Style.Container>
        <ScreenHeader color={route?.colors ?? undefined}>
          {route
            ? getCompetitionRouteDisplay({
                route,
                categoryUUIDs: selectedCategoryUUIDs.length
                  ? selectedCategoryUUIDs
                  : undefined,
                routeGroups: route?.competitionRouteGroups,
              }).name
            : null}
        </ScreenHeader>
        <Style.ContainerUpper>
          <CategorySelector
            categories={availableCategories}
            selectedCategories={selectedCategories}
            onChange={(categories) =>
              setSelectedCategoryUUIDs(categories.map((x) => x.uuid))
            }
          />
        </Style.ContainerUpper>
        <div
          className="text-danger d-flex align-items-center justify-content-center text-center"
          style={{ height: 300 }}
        >
          You do not have permission to view this route.
          <br /> Please contact the event organiser for any queries.
        </div>
      </Style.Container>
    );
  }

  return (
    <Style.Container>
      <ScreenHeader color={route?.colors ?? undefined}>
        {route
          ? getCompetitionRouteDisplay({
              route,
              categoryUUIDs: selectedCategoryUUIDs.length
                ? selectedCategoryUUIDs
                : undefined,
              routeGroups: route?.competitionRouteGroups,
            }).name
          : null}
      </ScreenHeader>
      <Style.ContainerUpper>
        <CategorySelector
          categories={availableCategories}
          selectedCategories={selectedCategories}
          onChange={(categories) =>
            setSelectedCategoryUUIDs(categories.map((x) => x.uuid))
          }
        />
      </Style.ContainerUpper>
      <Style.SearchRow>
        <TextInput
          type="text"
          value={searchText}
          placeholder="Search competitors..."
          onChange={(e) => setSearchText(e.target.value)}
          style={{ flex: 1 }}
        />
        <Dropdown align="end">
          <Dropdown.Toggle
            // as="button"
            style={{ width: 40, height: 40 }}
            variant="light"
            title="Sort"
            bsPrefix=" "
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="currentColor"
              viewBox="0 0 16 16"
            >
              <path
                fillRule="evenodd"
                d="M11.5 15a.5.5 0 0 0 .5-.5V2.707l1.146 1.147a.5.5 0 0 0 .708-.708l-2-1.999-.007-.007a.5.5 0 0 0-.707.006l-2 2a.5.5 0 1 0 .708.708L11 2.707V14.5a.5.5 0 0 0 .5.5m-7-14a.5.5 0 0 1 .5.5v11.793l1.146-1.147a.5.5 0 0 1 .708.708l-2 2a.5.5 0 0 1-.708 0l-2-2a.5.5 0 0 1 .708-.708L4 13.293V1.5a.5.5 0 0 1 .5-.5"
              />
            </svg>
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {sortOptions.map((opt) => (
              <Dropdown.Item
                key={opt.value}
                active={sortOption === opt.value}
                onClick={() => setSortOption(opt.value)}
              >
                {opt.label}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </Style.SearchRow>

      {queue?.length ? (
        <>
          <Style.QueueHeading>Queue ({queue?.length})</Style.QueueHeading>
          <DragDropContext
            onDragEnd={onDragEnd}
            onDragStart={() => setIsDragging(true)}
          >
            <StrictModeDroppable droppableId="queue">
              {({ placeholder, innerRef, droppableProps }) => (
                <Style.CompetitorsQueueList {...droppableProps} ref={innerRef}>
                  {queuedEntries?.map((entry, index) => (
                    <DraggableQueueItem
                      index={index}
                      key={entry.user.uuid}
                      entry={entry}
                      climbType={route?.climbType || "BOULDER"}
                      onRemove={onRemove}
                      route={route}
                    />
                  ))}
                  {placeholder}
                </Style.CompetitorsQueueList>
              )}
            </StrictModeDroppable>
          </DragDropContext>
        </>
      ) : null}

      <Style.CompetitorsList>
        {unqueuedEntries.map((entry) => (
          <Style.CompetitorsListItem key={entry.user.uuid}>
            <RouteEntryItem
              route={route}
              entry={entry}
              attempts={entry.attempts}
              climbType={route?.climbType || "BOULDER"}
            >
              <QueueStatusButton
                inQueue={entry.queueIndex !== -1}
                onClick={(e: SyntheticEvent<MouseEvent>) => {
                  e.preventDefault();
                  e.stopPropagation();
                  entry.queueIndex === -1 && addToQueue(entry.user.uuid);
                }}
              />
            </RouteEntryItem>
          </Style.CompetitorsListItem>
        ))}
      </Style.CompetitorsList>
    </Style.Container>
  );
}
